import * as Sentry from '@sentry/react-native';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';

import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { remoteConfigValues } from '@utils/remoteConfigValues';

interface UseFailedMessageTimeoutParams {
  createdAt: string | null | undefined;
  publishedAt: string | null | undefined;
  failedAt: string | null | undefined;
  isDraft: boolean | null | undefined;
  isRetry: boolean | null | undefined;
  clientId: string;
  chatId: string;
  timeoutThreshold?: number;
}

export const useFailedMessageTimeout = ({
  createdAt,
  publishedAt,
  failedAt,
  isDraft,
  isRetry,
  clientId,
  chatId,
  timeoutThreshold = 60,
}: UseFailedMessageTimeoutParams): boolean => {
  const [timeoutCheckTrigger, setTimeoutCheckTrigger] = useState(0);
  const { trackEvent } = useMixpanel();

  useEffect(() => {
    if (!remoteConfigValues().chat_failed_messages_indicator) return;
    if (failedAt || publishedAt || !createdAt || !isDraft) return;

    const secondsSinceCreated = dayjs().diff(dayjs(createdAt), 'second');

    if (secondsSinceCreated >= timeoutThreshold) return;

    const timeUntilTimeout = (timeoutThreshold - secondsSinceCreated) * 1000;
    const timeoutId = setTimeout(() => {
      setTimeoutCheckTrigger((prev) => prev + 1);

      Sentry.captureMessage('Message stuck in sending state', {
        level: 'warning',
        // Without an explicit fingerprint these group by an anonymous stack
        // frame and surface in Sentry under the title "anonymous"
        // (FRONTEND-291), which hid 20 affected users during the TA-10443
        // investigation. Group them all under one named issue instead.
        fingerprint: ['message-stuck-in-sending-state'],
        extra: {
          clientId,
          chatId,
          timeSinceCreated: timeoutThreshold,
          isDraft,
          isRetry,
          platform: Platform.OS,
        },
      });

      trackEvent(MIXPANEL_EVENTS.UI_INTERACTION_TIMEOUT, {
        interaction_type: 'message_send',
        timeout_seconds: timeoutThreshold,
        client_id: clientId,
        chat_id: chatId,
      });
    }, timeUntilTimeout);

    return () => clearTimeout(timeoutId);
  }, [
    createdAt,
    publishedAt,
    failedAt,
    isDraft,
    clientId,
    isRetry,
    chatId,
    timeoutThreshold,
    trackEvent,
  ]);

  const failedMessage = useMemo(() => {
    if (!remoteConfigValues().chat_failed_messages_indicator) return false;
    return (
      failedAt ||
      (createdAt &&
        isDraft &&
        !publishedAt &&
        dayjs().diff(dayjs(createdAt), 'second') >= timeoutThreshold)
    );
  }, [
    failedAt,
    createdAt,
    isDraft,
    publishedAt,
    timeoutCheckTrigger,
    timeoutThreshold,
  ]);

  return failedMessage;
};
